import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Upload, Check, AlertCircle, Palette } from 'lucide-react';

const AdminSettings: React.FC = () => {
  const { logo, updateLogo } = useAuth();
  const { primaryColor, secondaryColor, font, updateTheme, availableFonts } = useTheme();
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (file: File) => {
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      updateLogo(result);
      setSuccess('Logo updated successfully');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleThemeChange = (key: string, value: string) => {
    updateTheme({ [key]: value });
    setSuccess('Theme updated successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Settings</h2>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Theme Settings */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Theme Settings</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Font Family</label>
                <select
                  value={font}
                  onChange={(e) => handleThemeChange('font', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  style={{ fontFamily: font }}
                >
                  {availableFonts.map((fontName) => (
                    <option key={fontName} value={fontName} style={{ fontFamily: fontName }}>
                      {fontName}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-sm text-gray-500">
                  Preview text with {font} font
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Primary Color</label>
                <div className="mt-1 flex items-center space-x-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => handleThemeChange('primaryColor', e.target.value)}
                    className="h-8 w-8 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => handleThemeChange('primaryColor', e.target.value)}
                    className="form-input"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    placeholder="#000000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Secondary Color</label>
                <div className="mt-1 flex items-center space-x-2">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => handleThemeChange('secondaryColor', e.target.value)}
                    className="h-8 w-8 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => handleThemeChange('secondaryColor', e.target.value)}
                    className="form-input"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    placeholder="#000000"
                  />
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Preview</h4>
                <div className="space-y-2">
                  <div 
                    className="p-4 rounded"
                    style={{ 
                      backgroundColor: primaryColor,
                      color: '#ffffff',
                      fontFamily: font
                    }}
                  >
                    Primary Color Sample Text
                  </div>
                  <div 
                    className="p-4 rounded"
                    style={{ 
                      backgroundColor: secondaryColor,
                      color: '#ffffff',
                      fontFamily: font
                    }}
                  >
                    Secondary Color Sample Text
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Logo Settings */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Hotel Logo</h3>

            {error && (
              <div className="mb-4 bg-red-50 p-4 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 bg-green-50 p-4 rounded-md flex items-center">
                <Check className="h-5 w-5 text-green-400 mr-2" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}

            <div
              className={`mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                isDragging 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-gray-300 hover:border-indigo-500'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="space-y-1 text-center">
                {logo ? (
                  <div className="mb-4">
                    <img
                      src={logo}
                      alt="Hotel Logo"
                      className="mx-auto h-32 w-auto"
                    />
                  </div>
                ) : (
                  <Upload
                    className="mx-auto h-12 w-12 text-gray-400"
                    strokeWidth={1}
                  />
                )}
                <div className="flex flex-col items-center text-sm text-gray-600">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition duration-150 ease-in-out"
                  >
                    Upload a file
                  </button>
                  <input
                    type="file"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileChange(file);
                      }
                      e.target.value = '';
                    }}
                    accept="image/*"
                  />
                  <p className="mt-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 5MB
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;